/**
 * Design tokens shared across every report section, matching the
 * ProdEvictionsReview dashboard's burnt-orange accent + stone neutrals.
 *
 * jsPDF's setFillColor/setDrawColor/setTextColor take 0-255 RGB triples,
 * not hex strings, so every color is pre-split here to avoid re-parsing
 * hex in every draw call.
 */

export type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

export const colors = {
  accent: hexToRgb('#C2410C'),       // burnt orange — section headers, peak bars
  accentMid: hexToRgb('#FB923C'),
  accentLight: hexToRgb('#FDBA74'),
  accentLighter: hexToRgb('#FED7AA'),
  stone900: hexToRgb('#1C1917'),     // primary text
  stone700: hexToRgb('#57534E'),
  stone500: hexToRgb('#78716C'),     // secondary text / labels
  stone400: hexToRgb('#A8A29E'),     // muted labels, axis text
  stone200: hexToRgb('#E7E5E4'),     // borders, gridlines
  stone50: hexToRgb('#FAFAF9'),      // card backgrounds, bar tracks
  white: hexToRgb('#FFFFFF'),
  red700: hexToRgb('#B91C1C'),       // negative trend / extreme risk
  green700: hexToRgb('#15803D'),     // positive trend (fewer evictions)
  extreme700: hexToRgb('#7F1D1D'),
  extremeBg: hexToRgb('#FEF2F2'),
  high700: hexToRgb('#C2410C'),
  highBg: hexToRgb('#FFF7ED'),
  medium700: hexToRgb('#B45309'),
  mediumBg: hexToRgb('#FFFBEB'),
  low700: hexToRgb('#57534E'),
  mapNoData: hexToRgb('#F0EFED'),  // districts with no risk data on record
  mapStroke: hexToRgb('#FFFFFF'),
} as const;

/**
 * jsPDF core fonts only (Helvetica/Times/Courier) unless custom fonts are
 * embedded via doc.addFileToVFS + doc.addFont with base64 TTF data.
 * Swap `body`/`mono` for the embedded font names once Inter and
 * JetBrains Mono are exported as base64 and registered — the draw
 * primitives all read from this object rather than hardcoding font names,
 * so that's a one-line change when the assets are ready.
 */
export const fonts = {
  voice: 'helvetica', // stand-in for Fraunces until embedded
  body: 'helvetica',  // stand-in for Inter until embedded
  mono: 'courier',    // stand-in for JetBrains Mono until embedded
} as const;

export const layout = {
  pageMarginX: 32,
  cardGap: 10,
  cardRadius: 4,
  sectionLabelSize: 9,
  sectionSubLabelSize: 7.5,
} as const;